import React, { useState, useEffect, useCallback } from "react";
import { apiClient, goToLogin } from "../api/client";
import MainLayout from "../layout/MainLayout";
import { message } from "antd";
import { FaChartBar, FaCheckCircle, FaClock, FaWallet, FaUniversity } from "react-icons/fa";
import { tokenStore } from "../api/tokenStore";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const StatusBadge = ({ status }) => {
  const isCompleted = status === "Completed";
  return (
    <span
      className="px-2 py-1 rounded-pill fw-semibold"
      style={{
        fontSize: "11px",
        background: isCompleted ? "rgba(27,187,107,0.12)" : "rgba(247,166,79,0.15)",
        color: isCompleted ? "#1bbb6b" : "#F7A64F",
      }}
    >
      {status}
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div
    className="border rounded-3 p-3 d-flex flex-column gap-1"
    style={{
      flex: 1,
      minWidth: "160px",
      background: "var(--card-bg)",
      borderColor: "var(--border-color)",
      fontFamily: "'Inter', 'Plus Jakarta Sans', sans-serif",
    }}
  >
    <div className="d-flex align-items-center gap-2">
      <span style={{ fontSize: "18px", color: color || "var(--accent-color)", display: "flex" }}>
        {typeof Icon === "string" ? Icon : <Icon />}
      </span>
      <span style={{ color: "var(--text-muted)", fontSize: "12px", fontWeight: 500 }}>{label}</span>
    </div>
    <span className="fw-bold fs-4" style={{ color: color || "var(--text-color)", letterSpacing: "-0.5px" }}>
      {value ?? "-"}
    </span>
  </div>
);

const AdminTransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingIfti, setDownloadingIfti] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/transaction-history");
      setTransactions(res.data.transactions || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      if (err.response?.status === 401) goToLogin();
      else if (err.response?.status === 403) setForbidden(true);
      else message.error("Failed to load transaction history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const token = tokenStore.getAccess();
      const resp = await fetch(`${API_BASE}/api/admin/transaction-history/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!resp.ok) throw new Error("PDF download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ARMF-Transaction-History.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success("PDF downloaded successfully");
    } catch (err) {
      message.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadIftiPdf = async () => {
    setDownloadingIfti(true);
    try {
      const token = tokenStore.getAccess();
      const resp = await fetch(`${API_BASE}/api/admin/transaction-history/ifti-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!resp.ok) throw new Error("IFTI PDF download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ARMF-IFTI-E-Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success("IFTI-E report downloaded successfully");
    } catch (err) {
      message.error("Failed to download IFTI-E report");
    } finally {
      setDownloadingIfti(false);
    }
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  const fmtTime = (d) =>
    new Date(d).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: true });

  if (forbidden) {
    return (
      <MainLayout expand={false} NavName="Transaction History">
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh", gap: "12px" }}>
          <span style={{ fontSize: "48px" }}>🔒</span>
          <h2 className="fw-bold">Access Denied</h2>
          <p style={{ color: "var(--text-muted)" }}>Your account is not authorised to view transaction history.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout expand={false} NavName="Transaction History" hideKyc={true}>
      <div className="container mt-4" style={{ maxWidth: "1300px" }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fs-3 fw-bold m-0">Remittance Transaction History</h1>
            <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>
              AUD to PKR International Transfers via Meezan Bank
            </span>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={fetchData} disabled={loading}>
              {loading ? "Refreshing…" : "↻ Refresh"}
            </button>
            <button
              className="btn btn-sm"
              style={{
                background: "linear-gradient(135deg, #0d3b2e, #1bbb6b)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 20px",
                fontWeight: 600,
                fontSize: "13px",
                opacity: downloading ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              onClick={handleDownloadPdf}
              disabled={downloading}
            >
              {downloading ? (
                <>⏳ Generating…</>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
            <button
              className="btn btn-sm"
              style={{
                background: "linear-gradient(135deg, #1a4a3a, #0d3b2e)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 20px",
                fontWeight: 600,
                fontSize: "13px",
                opacity: downloadingIfti ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              onClick={handleDownloadIftiPdf}
              disabled={downloadingIfti}
            >
              {downloadingIfti ? (
                <>⏳ Generating…</>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  IFTI-E Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="d-flex gap-3 mb-4 flex-wrap">
            <StatCard icon={FaChartBar} label="Total Transactions" value={summary.total} />
            <StatCard icon={FaCheckCircle} label="Completed" value={summary.completed} color="#1bbb6b" />
            <StatCard icon={FaClock} label="Pending" value={summary.pending} color="#F7A64F" />
            <StatCard
              icon={FaWallet}
              label="Total Volume (AUD)"
              value={`$${summary.totalAUD?.toLocaleString("en-AU", { minimumFractionDigits: 2 })}`}
              color="#0d3b2e"
            />
            <StatCard
              icon={FaUniversity}
              label="Total Volume (PKR)"
              value={`₨${summary.totalPKR?.toLocaleString("en-PK", { minimumFractionDigits: 2 })}`}
              color="#0d3b2e"
            />
          </div>
        )}

        {/* Transaction Table */}
        <div className="border rounded-3 overflow-hidden" style={{ background: "var(--card-bg)" }}>
          <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
            <h2 className="fs-6 fw-semibold m-0">All Remittance Transactions</h2>
            <div className="d-flex gap-2">
              <span className="px-2 py-1 rounded-pill fw-semibold" style={{ fontSize: "11px", background: "rgba(27,187,107,0.12)", color: "#1bbb6b" }}>
                {transactions.filter((t) => t.status === "Completed").length} completed
              </span>
              <span className="px-2 py-1 rounded-pill fw-semibold" style={{ fontSize: "11px", background: "rgba(247,166,79,0.15)", color: "#F7A64F" }}>
                {transactions.filter((t) => t.status === "Pending").length} pending
              </span>
            </div>
          </div>

          {loading ? (
            <div className="p-5 text-center" style={{ color: "var(--text-muted)" }}>Loading…</div>
          ) : (
            <div className="table-responsive">
              <table className="table mb-0" style={{ fontSize: "12px" }}>
                <thead>
                  <tr style={{ background: "var(--bg-color)" }}>
                    <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>#</th>
                    <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Reference</th>
                    <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Date & Time</th>
                    <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Sender</th>
                    <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Beneficiary</th>
                    <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Beneficiary IBAN</th>
                    <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Bank</th>
                    <th className="px-3 py-3 fw-semibold border-bottom text-end" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>AUD</th>
                    <th className="px-3 py-3 fw-semibold border-bottom text-end" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Fee (AUD)</th>
                    <th className="px-3 py-3 fw-semibold border-bottom text-end" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>FX Rate</th>
                    <th className="px-3 py-3 fw-semibold border-bottom text-end" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>PKR</th>
                    <th className="px-3 py-3 fw-semibold border-bottom text-center" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, idx) => (
                    <tr key={tx.ref} style={{ borderBottom: "1px solid var(--border-color)" }}>
                      <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{idx + 1}</td>
                      <td className="px-3 py-2">
                        <code style={{ fontSize: "11px", color: "var(--accent-color)" }}>{tx.ref}</code>
                      </td>
                      <td className="px-3 py-2">
                        <div className="fw-semibold" style={{ fontSize: "11px" }}>{fmtDate(tx.date)}</div>
                        <div style={{ color: "var(--text-muted)", fontSize: "10px" }}>{fmtTime(tx.date)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="fw-semibold" style={{ fontSize: "11px" }}>{tx.senderName}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="fw-semibold" style={{ fontSize: "11px" }}>{tx.beneficiaryName}</div>
                      </td>
                      <td className="px-3 py-2">
                        <code style={{ fontSize: "10px", color: "var(--text-muted)" }}>{tx.beneficiaryIBAN?.replace(/\s/g, '')}</code>
                      </td>
                      <td className="px-3 py-2" style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                        {tx.bank}
                      </td>
                      <td className="px-3 py-3 text-end fw-bold">{tx.amountAUD?.toLocaleString("en-AU", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-3 text-end" style={{ color: "var(--text-muted)" }}>{tx.feeAUD?.toFixed(2)}</td>
                      <td className="px-3 py-3 text-end" style={{ color: "var(--text-muted)" }}>{tx.fxRate?.toFixed(2)}</td>
                      <td className="px-3 py-3 text-end fw-semibold">{tx.amountPKR?.toLocaleString("en-PK", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-3 text-center">
                        <StatusBadge status={tx.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* IFTI-E Compliance Report */}
        <div className="mt-5 border rounded-3 overflow-hidden" style={{ background: "var(--card-bg)" }}>
          <div className="p-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h2 className="fs-6 fw-semibold m-0">IFTI-E Compliance Report</h2>
              <span style={{ color: "var(--text-muted)", fontSize: "10px" }}>
                AUSTRAC, International Funds Transfer Instructions (Electronic), AML/CTF Act 2006
              </span>
            </div>
            <span className="px-2 py-1 rounded-pill fw-semibold" style={{ fontSize: "10px", background: "rgba(13,59,46,0.1)", color: "#0d3b2e" }}>
              {transactions.length} reports
            </span>
          </div>
          <div className="table-responsive">
            <table className="table mb-0" style={{ fontSize: "11px" }}>
              <thead>
                <tr style={{ background: "var(--bg-color)" }}>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>IFTI Ref</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>Sender</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>DOB</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>ID</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>Sender Address</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>Beneficiary</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>City</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>BEN BIC</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>Purpose</th>
                  <th className="px-3 py-3 fw-semibold border-bottom text-end" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>AUD</th>
                  <th className="px-3 py-3 fw-semibold border-bottom text-center" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>TH</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid var(--border-color)" }}>
                    <td className="px-3 py-2">
                      <code style={{ fontSize: "10px", color: "#0d3b2e" }}>{tx.iftiRef}</code>
                    </td>
                    <td className="px-3 py-2 fw-semibold" style={{ fontSize: "10px" }}>{tx.senderName}</td>
                    <td className="px-3 py-2" style={{ fontSize: "10px", color: "var(--text-muted)" }}>{tx.senderDOB}</td>
                    <td className="px-3 py-2" style={{ fontSize: "10px" }}>
                      <span style={{ color: "var(--text-muted)" }}>{tx.senderIdType}</span>{" "}
                      <code style={{ fontSize: "10px" }}>{tx.senderIdNumber}</code>
                    </td>
                    <td className="px-3 py-2" style={{ fontSize: "10px", color: "var(--text-muted)", maxWidth: "180px" }}>{tx.senderAddress}</td>
                    <td className="px-3 py-2 fw-semibold" style={{ fontSize: "10px" }}>{tx.beneficiaryName}</td>
                    <td className="px-3 py-2" style={{ fontSize: "10px", color: "var(--text-muted)" }}>{tx.beneficiaryCity}</td>
                    <td className="px-3 py-2">
                      <code style={{ fontSize: "10px" }}>{tx.beneficiaryBIC}</code>
                    </td>
                    <td className="px-3 py-2" style={{ fontSize: "10px" }}>{tx.purpose}</td>
                    <td className="px-3 py-2 text-end fw-bold" style={{ fontSize: "10px" }}>
                      {tx.amountAUD?.toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span
                        className="px-2 py-1 rounded fw-bold"
                        style={{
                          fontSize: "9px",
                          background: tx.thresholdIndicator ? "rgba(220,53,69,0.15)" : "rgba(27,187,107,0.12)",
                          color: tx.thresholdIndicator ? "#dc3545" : "#1bbb6b",
                        }}
                      >
                        {tx.thresholdIndicator ? "YES" : "No"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-top" style={{ background: "var(--bg-color)", fontSize: "10px", color: "var(--text-muted)" }}>
            <strong>TH</strong> = Threshold (AUD 10,000+) &nbsp;|&nbsp; <strong>BEN BIC</strong> = Beneficiary SWIFT/BIC &nbsp;|&nbsp;
            Reports submitted to AUSTRAC within 10 business days per AML/CTF Act 2006, Section 45
          </div>
        </div>

        {/* Client Deposits & Account Balances Section */}
        <div className="mt-5 border rounded-3 overflow-hidden" style={{ background: "var(--card-bg)" }}>
          <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
            <h2 className="fs-6 fw-semibold m-0">Client Deposits & Account Balances</h2>
          </div>
          <div className="table-responsive">
            <table className="table mb-0" style={{ fontSize: "12px" }}>
              <thead>
                <tr style={{ background: "var(--bg-color)" }}>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Client Name</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Recent Deposit (AUD)</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Sent Amount (AUD)</th>
                  <th className="px-3 py-3 fw-semibold border-bottom" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Remaining Balance (AUD)</th>
                  <th className="px-3 py-3 fw-semibold border-bottom text-end" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Balance %</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: "Rabia Ejaz", email: "rabia.ejaz47@gmail.com", deposit: 3900, sent: 1463, bal: 2437, pct: 62 },
                  { name: "Kamran Awan", email: "kamran.awan92@gmail.com", deposit: 2500, sent: 1995, bal: 505, pct: 20 },
                  { name: "Muhammad Nadeem", email: "muhammad.nadeem381@gmail.com", deposit: 10000, sent: 1634, bal: 8366, pct: 83 },
                  { name: "Anwar UL Haq", email: "anwar.haq55@gmail.com", deposit: 1500, sent: 892, bal: 608, pct: 40 },
                  { name: "Hamza Shabbir", email: "hamza.shabbir804@gmail.com", deposit: 1200, sent: 1077, bal: 123, pct: 10 },
                  { name: "Asad Nawaz", email: "asad.nawaz713@gmail.com", deposit: 2000, sent: 1183, bal: 817, pct: 41 },
                ].map((client, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid var(--border-color)" }}>
                    <td className="px-3 py-3 fw-semibold" style={{ fontSize: "11px" }}>{client.name}</td>
                    <td className="px-3 py-3 fw-semibold text-success">${client.deposit.toLocaleString()}</td>
                    <td className="px-3 py-3 text-danger">-${client.sent.toLocaleString()}</td>
                    <td className="px-3 py-3 fw-bold">${client.bal.toLocaleString()}</td>
                    <td className="px-3 py-3 text-end">
                      <span
                        className="px-2 py-1 rounded fw-bold"
                        style={{
                          fontSize: "10px",
                          background: client.pct > 50 ? "rgba(27,187,107,0.15)" : client.pct > 20 ? "rgba(247,166,79,0.15)" : "rgba(220,53,69,0.15)",
                          color: client.pct > 50 ? "#1bbb6b" : client.pct > 20 ? "#F7A64F" : "#dc3545",
                        }}
                      >
                        {client.pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Spacer */}
        <div className="mb-5" />
      </div>
    </MainLayout>
  );
};

export default AdminTransactionHistory;
